import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Download, CalendarDays, ArrowRight, ArrowLeft } from "lucide-react";
import { formConfig, cta, brand } from "../../content/siteContent";
import { SectionHeading, CTAButton } from "./primitives";
import { API, useConfig, getUTMParams } from "../../hooks/useConfig";
import { track, EVENTS } from "../../lib/analytics";
import { fireConversion } from "../../lib/conversions";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Field = ({ label, id, error, children, required }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-display font-semibold text-navy mb-1.5">
      {label} {required && <span className="text-teal">*</span>}
    </label>
    {children}
    {error && (
      <p className="mt-1 text-xs text-[#b91c1c]" role="alert" data-testid={`error-${id}`}>
        {error}
      </p>
    )}
  </div>
);

const inputCls =
  "w-full bg-white border border-input rounded-sm px-4 py-3 text-sm text-navy placeholder:text-slate-ink/50 focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal transition-colors duration-200";

export const LeadForm = () => {
  const config = useConfig();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errors, setErrors] = useState({});
  const startedRef = useRef(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", work_email: "", job_title: "", organisation: "",
    phone: "", industry: "", role: "", transform_goal: "", consent: false,
    company_website: "", // honeypot
  });

  const update = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track(EVENTS.FORM_STARTED);
    }
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validateStep1 = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name.";
    if (!form.last_name.trim()) e.last_name = "Please enter your last name.";
    if (!form.work_email.trim()) e.work_email = "Please enter your work email.";
    else if (!emailRe.test(form.work_email)) e.work_email = "Please enter a valid email address.";
    if (!form.job_title.trim()) e.job_title = "Please enter your job title.";
    if (!form.organisation.trim()) e.organisation = "Please enter your organisation.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateStep1()) {
      setStep(2);
      track(EVENTS.FORM_STEP_COMPLETED, { step: 1 });
    }
  };

  const submit = async (intent) => {
    if (intent === "prospectus" && !form.consent) {
      setErrors((e) => ({ ...e, consent: "Please acknowledge the privacy statement to continue." }));
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        intent,
        ...getUTMParams(),
      });
      track(EVENTS.FORM_SUBMITTED, { intent });
      track(EVENTS.LEAD_CONVERTED, { intent });
      fireConversion("lead", {
        user: {
          email: form.work_email,
          phone: form.phone,
          first_name: form.first_name,
          last_name: form.last_name,
        },
      });
      if (intent === "consultation") {
        navigate("/prospectus/thank-you?type=consultation");
      } else {
        navigate("/prospectus/thank-you");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setErrors((e) => ({ ...e, submit: msg }));
      setStatus("error");
    }
  };

  const onProspectusDownload = () => {
    track(EVENTS.PROSPECTUS_DOWNLOADED);
  };
  const onCalendarOpen = () => {
    track(EVENTS.CALENDAR_OPENED);
  };

  return (
    <section id="prospectus" data-testid="lead-form-section" className="bg-navy py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 blueprint opacity-50" />
      <div className="container-page relative grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="Request the Prospectus" dark>
            {brand.masterMessage}
          </SectionHeading>
          <p className="mt-6 text-white/70 text-base leading-relaxed max-w-md">
            Request the 2026 Prospectus to see the full transformation system, the simulation and the
            qualification pathway — then book your Pre-Training Review to discuss your goals.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <span className="font-display font-extrabold text-gold text-sm tracking-[0.2em] uppercase">
              {brand.tagline}
            </span>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-offwhite border border-hair rounded-sm p-7 lg:p-10">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="form-success"
                  className="text-center py-8"
                >
                  <CheckCircle2 className="h-12 w-12 text-teal mx-auto" />
                  <h3 className="mt-5 font-display font-bold text-navy text-2xl tracking-tight">
                    Thank you — your request is confirmed.
                  </h3>
                  <p className="mt-3 text-slate-ink text-sm leading-relaxed max-w-md mx-auto">
                    We've sent a confirmation to your inbox, and a member of our team will be in touch
                    within 24 business hours. In the meantime, you can access the prospectus or book your
                    Pre-Training Review below.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                    {config.prospectus_url ? (
                      <CTAButton
                        as="a"
                        href={config.prospectus_url}
                        target="_blank"
                        rel="noopener"
                        data-testid="download-prospectus"
                        onClick={onProspectusDownload}
                        withArrow
                      >
                        <Download className="h-4 w-4" /> Download the Prospectus
                      </CTAButton>
                    ) : (
                      <p className="text-slate-ink/70 text-xs self-center">
                        Prospectus download link will be provided by email.
                      </p>
                    )}
                    {config.calendar_url && (
                      <CTAButton
                        as="a"
                        variant="outlineDark"
                        href={config.calendar_url}
                        target="_blank"
                        rel="noopener"
                        data-testid="open-calendar"
                        onClick={onCalendarOpen}
                      >
                        <CalendarDays className="h-4 w-4" /> {cta.consultation}
                      </CTAButton>
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {/* Step indicator */}
                  <div className="flex items-center gap-3 mb-8">
                    {[1, 2].map((s) => (
                      <div key={s} className="flex items-center gap-3">
                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-sm text-xs font-display font-bold ${
                            step >= s ? "bg-teal text-white" : "bg-white border border-hair text-slate-ink"
                          }`}
                        >
                          {s}
                        </span>
                        {s === 1 && <span className="h-px w-10 bg-hair" />}
                      </div>
                    ))}
                    <span className="text-xs text-slate-ink ml-1">
                      Step {step} of 2 — {step === 1 ? "About you" : "Your context"}
                    </span>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={form.company_website}
                    onChange={(e) => update("company_website", e.target.value)}
                    className="hidden"
                    name="company_website"
                  />

                  {step === 1 ? (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="First name" id="first_name" error={errors.first_name} required>
                        <input id="first_name" data-testid="input-first-name" className={inputCls}
                          value={form.first_name} onChange={(e) => update("first_name", e.target.value)} />
                      </Field>
                      <Field label="Last name" id="last_name" error={errors.last_name} required>
                        <input id="last_name" data-testid="input-last-name" className={inputCls}
                          value={form.last_name} onChange={(e) => update("last_name", e.target.value)} />
                      </Field>
                      <Field label="Work email" id="work_email" error={errors.work_email} required>
                        <input id="work_email" type="email" data-testid="input-work-email" className={inputCls}
                          value={form.work_email} onChange={(e) => update("work_email", e.target.value)} />
                      </Field>
                      <Field label="Job title" id="job_title" error={errors.job_title} required>
                        <input id="job_title" data-testid="input-job-title" className={inputCls}
                          value={form.job_title} onChange={(e) => update("job_title", e.target.value)} />
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Organisation" id="organisation" error={errors.organisation} required>
                          <input id="organisation" data-testid="input-organisation" className={inputCls}
                            value={form.organisation} onChange={(e) => update("organisation", e.target.value)} />
                        </Field>
                      </div>
                      <div className="sm:col-span-2 mt-2">
                        <CTAButton className="w-full" data-testid="form-next" onClick={next} withArrow>
                          Continue
                        </CTAButton>
                      </div>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Phone" id="phone" error={errors.phone}>
                        <input id="phone" type="tel" data-testid="input-phone" className={inputCls}
                          value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                      </Field>
                      <Field label="Industry" id="industry">
                        <select id="industry" data-testid="input-industry" className={inputCls}
                          value={form.industry} onChange={(e) => update("industry", e.target.value)}>
                          <option value="">Select…</option>
                          {formConfig.industries.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Which role best describes you?" id="role">
                          <select id="role" data-testid="input-role" className={inputCls}
                            value={form.role} onChange={(e) => update("role", e.target.value)}>
                            <option value="">Select…</option>
                            {formConfig.roles.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="What are you looking to transform?" id="transform_goal">
                          <textarea id="transform_goal" data-testid="input-transform-goal" rows={3} className={inputCls}
                            value={form.transform_goal} onChange={(e) => update("transform_goal", e.target.value)} />
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input type="checkbox" data-testid="input-consent" className="mt-1 h-4 w-4 accent-[#0AADBD]"
                            checked={form.consent} onChange={(e) => update("consent", e.target.checked)} />
                          <span className="text-xs text-slate-ink leading-relaxed">
                            I acknowledge that my details will be used to respond to this request and share
                            program information, in line with the privacy statement.
                          </span>
                        </label>
                        {errors.consent && (
                          <p className="mt-1 text-xs text-[#b91c1c]" role="alert" data-testid="error-consent">{errors.consent}</p>
                        )}
                      </div>

                      {errors.submit && (
                        <p className="sm:col-span-2 text-sm text-[#b91c1c]" role="alert" data-testid="error-submit">
                          {errors.submit}
                        </p>
                      )}

                      <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 mt-2">
                        <button
                          type="button"
                          data-testid="form-back"
                          onClick={() => setStep(1)}
                          className="inline-flex items-center gap-2 text-sm font-display font-semibold text-navy px-4 py-3.5 rounded-sm border border-hair hover:bg-white transition-colors duration-200 focus-ring"
                        >
                          <ArrowLeft className="h-4 w-4" /> Back
                        </button>
                        <CTAButton
                          className="flex-1"
                          data-testid="form-submit"
                          disabled={status === "loading"}
                          onClick={() => submit("prospectus")}
                        >
                          {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send Me the Prospectus <ArrowRight className="h-4 w-4" /></>}
                        </CTAButton>
                      </div>
                      <button
                        type="button"
                        data-testid="form-consultation"
                        disabled={status === "loading"}
                        onClick={() => submit("consultation")}
                        className="sm:col-span-2 text-sm text-teal font-display font-semibold hover:underline focus-ring rounded-sm py-1"
                      >
                        Or, book your Pre-Training Review instead
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
